import React, { useState, useEffect, useRef } from 'react';

/**
 * Reusable AnimatedText component.
 * Re-triggers a smooth fade + upward slide animation whenever `text` changes,
 * on initial mount, or when scrolled back into view via IntersectionObserver.
 */
const AnimatedText = ({
  text,
  className = '',
  as: Component = 'p',
  delay = 0,
  duration = 700,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const containerRef = useRef(null);

  // Re-trigger animation when text content changes
  useEffect(() => {
    setAnimKey((prev) => prev + 1);
  }, [text]);

  // Re-play animation when scrolled into view using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.15 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="overflow-hidden">
      <Component
        key={`${animKey}-${text}-${isVisible}`}
        style={{
          animationDuration: `${duration}ms`,
          animationDelay: `${delay}ms`,
          animationFillMode: 'forwards',
        }}
        className={`${className} ${
          isVisible ? 'animate-fade-slide-up' : 'opacity-0'
        }`}
      >
        {text}
      </Component>
    </div>
  );
};

export default AnimatedText;
